import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Close,
  GroupAdd as GroupAddIcon,
  DeleteOutline as DeleteOutlineIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { getPortalAccess, canLoadProviderScopedData, resolveProviderId } from '../utils/portalAccess';
import { authService } from '../services/auth';
import {
  Transportation as TransportationType,
  Location,
  Provider,
  Client,
  Service,
  TransportationParentWithTransportations,
  ProviderVehicle,
} from '../types';
import { runTransportationRowsOverlapCheck } from '../utils/scheduleOverlap';
import TransportationLocationDialog from '../components/TransportationLocationDialog';
import { easternTodayYmd } from '../utils/easternTime';
import { fieldOnPageBlueSx, selectMenuWhitePaperProps } from '../mui/fieldOnPageBlue';
import AppDateField from '../components/AppDateField';
import { getClientDisplayName, loadClientsForRecords } from '../utils/clientDisplay';

/** Admin dialog adds provider selection; other fields match provider-portal transportation entry. */
interface TransportationFormData {
  provider_id: number | string;
  client_id: number | string;
  vehicle_id: number | string;
  transportation_date?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  starting_miles?: number | string;
  notes?: string;
}

interface LocationFormData {
  location_address_line_1: string;
  location_address_line_2: string;
  location_city: string;
  location_state: string;
  location_zip: string;
  location_country: string;
  location_latitude: number | undefined;
  location_longitude: number | undefined;
}

const ROUTE_DETAILS_VERSION = 1 as const;

interface RouteStopState {
  location_id: number | '';
  label?: string;
}

const EMPTY_ROUTE_STOP: RouteStopState = { location_id: '' };

function routeStopLocationIdToNumber(id: RouteStopState['location_id'] | undefined): number {
  if (id === undefined || id === '') return 0;
  return id;
}

function defaultStopLocationName(index: number): string {
  if (index === 0) return 'Start Location';
  return `Stop ${index}`;
}

/** When a client has exactly one transportation service, return its id for autofill. */
function soleTransportationServiceId(services: Service[]): number | '' {
  if (services.length !== 1) return '';
  const sid = Number(services[0].service_id);
  return sid > 0 && !Number.isNaN(sid) ? sid : '';
}

function segmentOrdinalWord(n: number): string {
  const words = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
  return words[n] || String(n);
}

const NARROW_SEGMENT_HEADER = '#C62828';
const NARROW_ACTION_PURPLE = '#7E57C2';
const NARROW_ACTION_PURPLE_HOVER = '#6A4FB0';

export interface LegPassenger {
  client_id: number;
  service_id: number;
  support_ids?: number[];
}

type DraftPassenger = { client_id: number | ''; service_id: number | ''; support_ids?: number[] };

type ClientSupportOption = {
  support_id: number;
  support_title?: string;
  service_id?: number;
  active_status?: boolean;
};

function asList(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown[] }).items)) {
    return (value as { items: any[] }).items;
  }
  return [];
}

function supportsForSelection(
  options: ClientSupportOption[] | undefined,
  serviceId: number | '',
  selected: number[]
): ClientSupportOption[] {
  const visible = (options || []).filter(
    support => support.active_status !== false || selected.includes(support.support_id)
  );
  const sid = serviceId === '' ? 0 : Number(serviceId);
  if (!sid) return visible;
  const matched = visible.filter(
    support => support.service_id === sid || selected.includes(support.support_id)
  );
  return matched.length > 0 ? matched : visible;
}

interface RouteLegState {
  passengers: LegPassenger[];
  odometer_at_destination?: string;
}

interface RouteDetailsV1 {
  v: typeof ROUTE_DETAILS_VERSION;
  stops: { location_id: number; label?: string }[];
  legs: {
    client_ids?: number[];
    passengers?: { client_id: number; service_id?: number; support_ids?: number[] }[];
    segment_miles?: string;
    odometer_at_destination?: string;
  }[];
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString || typeof dateString !== 'string') return 'N/A';
  try {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return 'Invalid date';
    return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}-${year}`;
  } catch {
    return 'Invalid date';
  }
};

const transportationOccurrenceDateYmd = (occ: TransportationParentWithTransportations): string => {
  const t = (occ.transportations || [])[0];
  if (!t) return '';
  const raw = t.transportation_date || t.created_at || '';
  return typeof raw === 'string' ? raw.split('T')[0] || '' : '';
};

const formatLocation = (location: Location | undefined) => {
  if (!location) return '';
  const parts = [
    location.location_address_line_1,
    location.location_city,
    location.location_state,
    location.location_zip,
  ].filter(Boolean);
  return parts.join(', ').toUpperCase();
};

function legPassengersFromJsonLeg(l: RouteDetailsV1['legs'][0]): LegPassenger[] {
  if (Array.isArray(l.passengers) && l.passengers.length > 0) {
    return l.passengers
      .map(p => ({
        client_id: Number(p.client_id),
        service_id: p.service_id != null && p.service_id !== undefined ? Number(p.service_id) : 0,
        support_ids: Array.isArray(p.support_ids) ? p.support_ids.map(Number).filter(id => id > 0) : [],
      }))
      .filter(p => p.client_id > 0);
  }
  if (Array.isArray(l.client_ids) && l.client_ids.length > 0) {
    return l.client_ids.map(id => ({ client_id: Number(id), service_id: 0 }));
  }
  return [];
}

function parseRouteFromTransportation(t: TransportationType): {
  stops: RouteStopState[];
  legs: RouteLegState[];
  firstStopPassengers: LegPassenger[];
} {
  if (t.details) {
    try {
      const raw = JSON.parse(t.details) as Partial<RouteDetailsV1>;
      if (raw.v === ROUTE_DETAILS_VERSION && Array.isArray(raw.stops) && Array.isArray(raw.legs)) {
        const stops: RouteStopState[] = raw.stops.map(s => ({
          location_id: s.location_id ?? '',
          label: s.label,
        }));
        let legs: RouteLegState[] = raw.legs.map(l => {
          const dest =
            l.odometer_at_destination != null && String(l.odometer_at_destination) !== ''
              ? String(l.odometer_at_destination)
              : l.segment_miles != null && l.segment_miles !== ''
                ? String(l.segment_miles)
                : '';
          return {
            passengers: legPassengersFromJsonLeg(l),
            odometer_at_destination: dest,
          };
        });
        const st: RouteStopState[] = stops.length > 0 ? stops : [{ ...EMPTY_ROUTE_STOP }];
        while (legs.length < st.length - 1) {
          legs.push({ passengers: [], odometer_at_destination: '' });
        }
        legs = legs.slice(0, Math.max(0, st.length - 1));
        return { stops: st, legs, firstStopPassengers: [] };
      }
    } catch {
      /* fall through */
    }
  }
  const stops: RouteStopState[] = [];
  if (t.start_location_id) stops.push({ location_id: t.start_location_id });
  if (t.end_location_id && t.end_location_id !== t.start_location_id) {
    stops.push({ location_id: t.end_location_id });
  }
  if (stops.length === 0) stops.push({ ...EMPTY_ROUTE_STOP });
  const legs: RouteLegState[] = [];
  const p0: LegPassenger[] =
    t.client_id && t.service_id
      ? [{ client_id: Number(t.client_id), service_id: Number(t.service_id) }]
      : t.client_id
        ? [{ client_id: Number(t.client_id), service_id: 0 }]
        : [];
  if (stops.length >= 2) {
    legs.push({
      passengers: p0.length > 0 ? p0 : [],
      odometer_at_destination:
        t.ending_miles != null && t.ending_miles !== undefined ? String(t.ending_miles) : '',
    });
    for (let i = 1; i < stops.length - 1; i += 1) {
      legs.push({ passengers: p0.length > 0 ? p0.map(x => ({ ...x })) : [], odometer_at_destination: '' });
    }
  }
  return {
    stops,
    legs,
    firstStopPassengers:
      stops.length === 1 && t.client_id
        ? t.service_id
          ? [{ client_id: Number(t.client_id), service_id: Number(t.service_id) }]
          : [{ client_id: Number(t.client_id), service_id: 0 }]
        : [],
  };
}

function parseRouteFromTransportationRows(rows: TransportationType[]): {
  stops: RouteStopState[];
  legs: RouteLegState[];
  firstStopPassengers: LegPassenger[];
} {
  const sorted = [...rows]
    .filter(r => r.start_location_id && r.end_location_id)
    .sort((a, b) => Number(a.transportation_id) - Number(b.transportation_id));
  if (sorted.length === 0) {
    return { stops: [{ ...EMPTY_ROUTE_STOP }], legs: [], firstStopPassengers: [] };
  }
  const legs: RouteLegState[] = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i].start_location_id;
    const end = sorted[i].end_location_id;
    const passengers: LegPassenger[] = [];
    let odometerDest = '';
    while (
      i < sorted.length &&
      sorted[i].start_location_id === start &&
      sorted[i].end_location_id === end
    ) {
      const r = sorted[i];
      passengers.push({
        client_id: Number(r.client_id),
        service_id: r.service_id != null && r.service_id !== undefined ? Number(r.service_id) : 0,
        support_ids: Array.isArray(r.support_ids) ? r.support_ids.map(Number).filter(id => id > 0) : [],
      });
      if (odometerDest === '' && r.ending_miles != null && r.ending_miles !== undefined) {
        odometerDest = String(r.ending_miles);
      }
      i++;
    }
    legs.push({ passengers, odometer_at_destination: odometerDest });
  }
  const stops: RouteStopState[] = [];
  if (legs.length > 0) {
    stops.push({ location_id: sorted[0].start_location_id as number });
    let idx = 0;
    for (let L = 0; L < legs.length; L++) {
      const endRow = sorted[idx];
      stops.push({ location_id: endRow.end_location_id as number });
      idx += legs[L].passengers.length;
    }
  }
  return { stops, legs, firstStopPassengers: [] };
}

function parseOptionalOdometer(v: string | number | undefined | null): number | null {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(String(v).trim());
  return Number.isNaN(n) ? null : n;
}

/** FastAPI often returns `detail` as a list of objects. Rendering that list crashes the page. */
function apiErrorMessage(err: unknown, fallback: string): string {
  const ex = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = ex?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          const rec = item as { msg?: unknown; loc?: unknown };
          const loc = Array.isArray(rec.loc) ? rec.loc.filter(part => part !== 'body').join('.') : '';
          const msg = String(rec.msg ?? '');
          return loc ? `${loc}: ${msg}` : msg;
        }
        return '';
      })
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  if (ex?.message) return ex.message;
  return fallback;
}

function lineMiles(t: TransportationType): number | null {
  if (t.miles != null && t.miles !== undefined) {
    const n = Number(t.miles);
    return Number.isNaN(n) ? null : n;
  }
  if (t.starting_miles != null && t.ending_miles != null) {
    const d = Number(t.ending_miles) - Number(t.starting_miles);
    return Number.isNaN(d) ? null : d;
  }
  return null;
}

function totalParentTripMiles(rows: TransportationType[]): number | null {
  if (!rows.length) return null;
  const byLeg = new Map<string, number>();
  for (const r of rows) {
    if (r.start_location_id == null || r.end_location_id == null) continue;
    const key = `${Number(r.start_location_id)}-${Number(r.end_location_id)}`;
    const m = lineMiles(r);
    if (m != null) byLeg.set(key, m);
  }
  if (byLeg.size === 0) return null;
  return Array.from(byLeg.values()).reduce((a, v) => a + v, 0);
}

const LINE_BG = '#2D4B73';
const ENTERED_GREEN = '#4CAF50';
const ENTERED_GREEN_HOVER = '#43A047';

function enteredActionButtonSx(entered: boolean, extra?: Record<string, unknown>) {
  return {
    ...extra,
    ...(entered
      ? {
          bgcolor: ENTERED_GREEN,
          color: '#fff',
          '&:hover': { bgcolor: ENTERED_GREEN_HOVER },
        }
      : {}),
  };
}

function routeActionButtonProps(entered: boolean, missing: boolean, extra?: Record<string, unknown>) {
  if (missing) {
    return {
      color: 'error' as const,
      variant: 'outlined' as const,
      sx: { ...extra, borderWidth: 2 },
    };
  }
  return {
    color: 'primary' as const,
    variant: (entered ? 'contained' : 'outlined') as 'contained' | 'outlined',
    sx: enteredActionButtonSx(entered, extra),
  };
}

const RouteNodeMarker = () => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: 'linear-gradient(145deg, #ffffff 0%, #e8eef6 55%, #dce6f2 100%)',
      border: '1px solid rgba(45, 75, 115, 0.28)',
      boxShadow: '0 2px 10px rgba(45, 75, 115, 0.22), 0 1px 3px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      position: 'relative',
      zIndex: 1,
    }}
  >
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        border: `3px solid ${LINE_BG}`,
        bgcolor: 'transparent',
        boxSizing: 'border-box',
        boxShadow: 'inset 0 1px 2px rgba(45, 75, 115, 0.12)',
      }}
    />
  </Box>
);

/** Thin vertical connector; must be column-flex so width stays narrow. */
const RouteConnectorLine = ({ minHeight = 72 }: { minHeight?: number }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'stretch',
      width: '100%',
      minHeight,
      pt: 0,
      pb: 0,
      mt: '-12px',
      mb: '-12px',
      position: 'relative',
      zIndex: 0,
    }}
  >
    <Box
      sx={{
        width: 3,
        flex: '1 1 auto',
        minHeight,
        bgcolor: LINE_BG,
        borderRadius: '1px',
        alignSelf: 'center',
      }}
    />
  </Box>
);

const SegmentPassengerSummary = ({
  passengers,
  getClientName,
  getServiceCode,
}: {
  passengers: LegPassenger[];
  getClientName: (id: number | string) => string;
  getServiceCode: (id: number | undefined) => string;
}) => {
  const rows =
    passengers.length > 0
      ? passengers.map((p, idx) => ({
          key: `${p.client_id}-${idx}`,
          text: `Client ${idx + 1}: ${getClientName(p.client_id)} – ${getServiceCode(p.service_id)}`,
        }))
      : [{ key: 'none', text: 'Client 1: None Selected' }];
  return (
    <Stack spacing={0.5} sx={{ mt: 0.25, mb: 1 }}>
      {rows.map(row => (
        <Typography key={row.key} variant="body2" component="div">
          {row.text}
        </Typography>
      ))}
    </Stack>
  );
};

const NarrowClientList = ({
  passengers,
  getClientName,
}: {
  passengers: LegPassenger[];
  getClientName: (id: number | string) => string;
}) => (
  <Box sx={{ mt: 0.75, mb: 2 }}>
    <Typography variant="body2" sx={{ mb: 0.25 }}>
      Clients on Segment:
    </Typography>
    {passengers.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        - None selected
      </Typography>
    ) : (
      passengers.map(p => (
        <Typography key={p.client_id} variant="body2">
          {`- ${getClientName(p.client_id)}`}
        </Typography>
      ))
    )}
  </Box>
);

interface TransportationProps {
  selectedProvider?: number | null;
  /** `addDialogOnly`: list/filter hidden; add transportation dialog opens immediately. */
  entryVariant?: 'full' | 'addDialogOnly';
  /** When `entryVariant` is `addDialogOnly`, called when the add dialog is dismissed or after a successful save. */
  onAddEntryClose?: () => void;
  /** When set (typically with `addDialogOnly`), prefill schedule from the visit and set `visit_id` on create. */
  visitLink?: {
    visitIdByClientId: Record<number, number>;
    providerId: number;
    transportationDate?: string;
    scheduledStart?: string;
    scheduledEnd?: string;
    clientIds?: number[];
  } | null;
  /**
   * When set, Save validates and returns rows to the caller instead of POSTing
   * (used when attaching transportation to an unsaved visit).
   */
  onDraftSave?: (rows: Record<string, unknown>[]) => void;
  /** Previously captured draft rows to re-open for editing (Change). */
  initialDraftRows?: Record<string, unknown>[] | null;
}

const TransportationPage: React.FC<TransportationProps> = ({
  selectedProvider,
  entryVariant = 'full',
  onAddEntryClose,
  visitLink = null,
  onDraftSave,
  initialDraftRows = null,
}) => {
  const isAddDialogOnly = entryVariant === 'addDialogOnly';
  const visitLinkRef = useRef(visitLink);
  visitLinkRef.current = visitLink;
  const onDraftSaveRef = useRef(onDraftSave);
  onDraftSaveRef.current = onDraftSave;
  const initialDraftRowsRef = useRef(initialDraftRows);
  initialDraftRowsRef.current = initialDraftRows;
  const [transportationOccurrences, setTransportationOccurrences] = useState<TransportationParentWithTransportations[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [vehicles, setVehicles] = useState<ProviderVehicle[]>([]);
  const [transportationEligibleClientIds, setTransportationEligibleClientIds] = useState<Set<number>>(new Set());
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [serviceOptionsByClient, setServiceOptionsByClient] = useState<Record<number, Service[]>>({});
  const [supportsByClient, setSupportsByClient] = useState<Record<number, ClientSupportOption[]>>({});
  const supportsLoadedRef = useRef<Set<number>>(new Set());
  const [locationsMap, setLocationsMap] = useState<Record<number, Location>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [abandonMainOpen, setAbandonMainOpen] = useState(false);
  const [editingTransportation, setEditingTransportation] = useState<TransportationType | null>(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationStopIndex, setLocationStopIndex] = useState(0);
  const [routeStops, setRouteStops] = useState<RouteStopState[]>([
    { ...EMPTY_ROUTE_STOP, label: defaultStopLocationName(0) },
    { ...EMPTY_ROUTE_STOP, label: defaultStopLocationName(1) },
  ]);
  const [routeLegs, setRouteLegs] = useState<RouteLegState[]>([
    { passengers: [], odometer_at_destination: '' },
  ]);
  const [firstStopPassengers, setFirstStopPassengers] = useState<LegPassenger[]>([]);
  const [passengersDialog, setPassengersDialog] = useState<{
    leg: 'first' | number;
    draft: DraftPassenger[];
  } | null>(null);
  const [passengersFieldErrors, setPassengersFieldErrors] = useState<
    Record<number, { client?: boolean; service?: boolean }>
  >({});
  const [passengersDialogError, setPassengersDialogError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TransportationFormData>({
    provider_id: '',
    client_id: '',
    vehicle_id: '',
    transportation_date: '',
    scheduled_start: '',
    scheduled_end: '',
    starting_miles: '',
    notes: '',
  });
  const [locationFormData, setLocationFormData] = useState<LocationFormData>({
    location_address_line_1: '',
    location_address_line_2: '',
    location_city: '',
    location_state: '',
    location_zip: '',
    location_country: 'US',
    location_latitude: undefined,
    location_longitude: undefined,
  });
  const [filters, setFilters] = useState({ providerId: '', clientId: '', startDate: '', endDate: '' });

  useEffect(() => {
    fetchData();
  }, [selectedProvider]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const access = await getPortalAccess();
      const currentUser = await authService.getCurrentUser();
      const providerId = resolveProviderId(
        access,
        selectedProvider,
        currentUser.attributes['custom:provider_id'],
      );
      if (!canLoadProviderScopedData(access, providerId)) {
        setTransportationOccurrences([]);
        setProviders([]);
        setClients([]);
        return;
      }
      const [transResp, provResp, servResp] = await Promise.all([
        api.transportationParents.list(0, 500, { providerId: providerId ?? undefined }),
        api.providers.getAll(),
        api.services.list(0, 500),
      ]);
      const occItems = transResp?.items || [];
      setTransportationOccurrences(occItems);
      const myTransportations: TransportationType[] = occItems.flatMap(
        (o: TransportationParentWithTransportations) => o.transportations || []
      );
      setProviders(Array.isArray(provResp) ? provResp : []);
      const servArray = Array.isArray(servResp) ? servResp : servResp?.items || [];
      setAllServices(servArray);
      const locationIds: number[] = Array.from(
        new Set(
          myTransportations.flatMap((x: TransportationType) =>
            [x.start_location_id, x.end_location_id].filter((id): id is number => !!id && id !== 0)
          )
        )
      );
      if (locationIds.length > 0) {
        const locResults = await Promise.all(locationIds.map((id: number) => api.locations.get(id)));
        const map: Record<number, Location> = {};
        locResults.forEach((loc: Location) => {
          if (loc?.location_id) map[loc.location_id] = loc;
        });
        setLocationsMap(map);
      }
      const loadedClients = await loadClientsForRecords(myTransportations, {
        providerId,
        listByProvider: api.clients.listByProvider,
      });
      setClients(loadedClients);
    } catch (err) {
      console.error('Error fetching transportation:', err);
      setError('Failed to load transportation records.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formData.provider_id && formData.provider_id !== '') {
      api.clients
        .listByProvider(formData.provider_id, 1000)
        .then((r: unknown) => {
          const formClients = Array.isArray(r) ? r : (r as { items?: Client[] })?.items || [];
          setClients(prev => {
            const byId = new Map(prev.map(c => [Number(c.client_id), c]));
            formClients.forEach((c: Client) => byId.set(Number(c.client_id), c));
            return Array.from(byId.values());
          });
        })
        .catch(() => {});
    }
  }, [formData.provider_id]);

  useEffect(() => {
    if (!formData.provider_id || formData.provider_id === '') {
      setVehicles([]);
      return;
    }
    const pid = Number(formData.provider_id);
    if (Number.isNaN(pid)) {
      setVehicles([]);
      return;
    }
    let cancelled = false;
    api.providerVehicles
      .list({ providerId: pid })
      .then((data: ProviderVehicle[]) => {
        if (cancelled) return;
        setVehicles(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setVehicles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.provider_id]);

  useEffect(() => {
    if (!formData.provider_id || formData.provider_id === '') {
      setTransportationEligibleClientIds(new Set());
      return;
    }
    const pid = Number(formData.provider_id);
    if (Number.isNaN(pid)) {
      setTransportationEligibleClientIds(new Set());
      return;
    }
    let cancelled = false;
    api.clientIspServices
      .getClientIdsWithTransportationIsp(pid)
      .then((ids: number[]) => {
        if (cancelled) return;
        setTransportationEligibleClientIds(new Set((ids || []).map(Number)));
      })
      .catch(() => {
        if (!cancelled) setTransportationEligibleClientIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [formData.provider_id]);

  const getFilteredTransportationOccurrences = (): TransportationParentWithTransportations[] => {
    let filtered = transportationOccurrences;
    if (selectedProvider) {
      filtered = filtered.filter(o => Number(o.provider_id) === selectedProvider);
    }
    if (filters.providerId) {
      filtered = filtered.filter(o => o.provider_id === Number(filters.providerId));
    }
    if (filters.clientId) {
      const cid = Number(filters.clientId);
      filtered = filtered.filter(o =>
        (o.transportations || []).some(t => Number(t.client_id) === cid)
      );
    }
    if (filters.startDate) {
      filtered = filtered.filter(o => {
        const ymd = transportationOccurrenceDateYmd(o);
        return ymd !== '' && ymd >= filters.startDate;
      });
    }
    if (filters.endDate) {
      filtered = filtered.filter(o => {
        const ymd = transportationOccurrenceDateYmd(o);
        return ymd !== '' && ymd <= filters.endDate;
      });
    }
    return filtered.sort((a, b) => {
      const ta = (a.transportations || [])[0];
      const tb = (b.transportations || [])[0];
      const dateA = String(ta?.transportation_date || ta?.created_at || '');
      const dateB = String(tb?.transportation_date || tb?.created_at || '');
      return dateB.localeCompare(dateA);
    });
  };

  const getProviderName = (id: number) => {
    const p = providers.find(x => Number(x.provider_id) === id);
    return p ? `${p.first_name} ${p.last_name}` : `Provider ${id}`;
  };
  const getClientName = (id: number | string) => getClientDisplayName(clients, id);
  const getServiceCode = (id: number | undefined) => {
    if (id == null || Number.isNaN(Number(id))) return 'N/A';
    const n = Number(id);
    const s =
      allServices.find(x => Number(x.service_id) === n) ||
      Object.values(serviceOptionsByClient)
        .flat()
        .find(x => Number(x.service_id) === n);
    if (s) return [s.code, s.service_unit].filter(Boolean).join(' | ');
    return 'N/A';
  };

  const extraClientIdsInForm = useMemo(() => {
    const ids = new Set<number>();
    if (editingTransportation) ids.add(Number(editingTransportation.client_id));
    routeLegs.forEach(l => l.passengers.forEach(p => ids.add(p.client_id)));
    firstStopPassengers.forEach(p => ids.add(p.client_id));
    return ids;
  }, [editingTransportation, routeLegs, firstStopPassengers]);

  const loadServicesForClient = useCallback(
    async (clientId: number, providerIdOverride?: number) => {
      const pid = providerIdOverride != null ? Number(providerIdOverride) : Number(formData.provider_id);
      if (!clientId || !pid) return;
      try {
        const r = await api.clientIspServices.getTransportationServicesForClientProvider(clientId, pid);
        const list = Array.isArray(r) ? r : (r as { items?: Service[] })?.items || [];
        setServiceOptionsByClient(prev => ({ ...prev, [clientId]: list }));
        const sole = soleTransportationServiceId(list);
        if (sole === '') return;
        setPassengersDialog(dialog => {
          if (!dialog) return dialog;
          let changed = false;
          const autofilledIdx: number[] = [];
          const draft = dialog.draft.map((row, idx) => {
            if (row.client_id === '' || Number(row.client_id) !== clientId) return row;
            if (row.service_id !== '' && Number(row.service_id) > 0) return row;
            changed = true;
            autofilledIdx.push(idx);
            return { ...row, service_id: sole };
          });
          if (!changed) return dialog;
          setPassengersFieldErrors(errs => {
            const next = { ...errs };
            autofilledIdx.forEach(idx => {
              if (!next[idx]) return;
              const { service: _s, ...rest } = next[idx];
              if (rest.client) next[idx] = rest;
              else delete next[idx];
            });
            if (!Object.values(next).some(e => e?.service)) setPassengersDialogError(null);
            return next;
          });
          return { ...dialog, draft };
        });
      } catch {
        setServiceOptionsByClient(prev => ({ ...prev, [clientId]: [] }));
      }
    },
    [formData.provider_id]
  );

  const loadSupportsForClient = useCallback(async (clientId: number) => {
    if (!clientId || supportsLoadedRef.current.has(clientId)) return;
    supportsLoadedRef.current.add(clientId);
    try {
      const ispRes = await api.clientISP.getByClient(clientId);
      const isps = asList(ispRes).filter((isp: any) => isp?.isp_id);
      const active = isps.filter((isp: any) => isp.active_status !== false);
      const chosen = active.length > 0 ? active : isps;
      const chunks = await Promise.all(
        chosen.map(async (isp: any) => {
          const [supportsRes, servicesRes] = await Promise.all([
            api.clientISP.supports.list(Number(isp.isp_id)),
            api.clientIspServices.getByIsp(isp.isp_id),
          ]);
          const services = asList(servicesRes);
          const serviceIdByIspService = new Map<number, number>();
          services.forEach((service: any) => {
            if (service?.isp_service_id != null && service?.service_id != null) {
              serviceIdByIspService.set(Number(service.isp_service_id), Number(service.service_id));
            }
          });
          return asList(supportsRes).map((support: any) => ({
            support_id: Number(support.support_id),
            support_title: support.support_title,
            service_id:
              support.isp_service_id != null
                ? serviceIdByIspService.get(Number(support.isp_service_id))
                : undefined,
            active_status: support.active_status,
          }));
        })
      );
      setSupportsByClient(prev => ({ ...prev, [clientId]: chunks.flat() }));
    } catch {
      supportsLoadedRef.current.delete(clientId);
      setSupportsByClient(prev => ({ ...prev, [clientId]: [] }));
    }
  }, []);

  const openPassengersDialog = (leg: 'first' | number) => {
    const pid = Number(formData.provider_id);
    if (!pid) {
      setError('Select a provider before adding clients.');
      return;
    }
    const source: LegPassenger[] = leg === 'first' ? firstStopPassengers : routeLegs[leg]?.passengers || [];
    const draft: DraftPassenger[] =
      source.length > 0
        ? source.map(p => ({
            client_id: p.client_id,
            service_id: p.service_id > 0 ? p.service_id : ('' as const),
            support_ids: p.support_ids || [],
          }))
        : [{ client_id: '', service_id: '', support_ids: [] }];
    setPassengersFieldErrors({});
    setPassengersDialogError(null);
    setPassengersDialog({ leg, draft });
    source.forEach(p => {
      if (p.client_id) {
        void loadServicesForClient(p.client_id);
        void loadSupportsForClient(p.client_id);
      }
    });
  };

  const applyPassengersDialog = () => {
    if (!passengersDialog) return;
    const { leg, draft } = passengersDialog;
    const fieldErrors: Record<number, { client?: boolean; service?: boolean }> = {};
    const done: LegPassenger[] = [];
    const seen = new Set<number>();
    let duplicate = false;

    draft.forEach((row, idx) => {
      const missingClient = row.client_id === '';
      const missingService = row.service_id === '';
      const rowEmpty = missingClient && missingService;
      if (rowEmpty) return;
      if (missingClient || missingService) {
        fieldErrors[idx] = {
          ...(missingClient ? { client: true } : {}),
          ...(missingService ? { service: true } : {}),
        };
        return;
      }
      const cid = Number(row.client_id);
      const sid = Number(row.service_id);
      if (seen.has(cid)) {
        duplicate = true;
        fieldErrors[idx] = { ...(fieldErrors[idx] || {}), client: true };
        return;
      }
      seen.add(cid);
      done.push({ client_id: cid, service_id: sid, support_ids: row.support_ids || [] });
    });

    if (done.length === 0 && Object.keys(fieldErrors).length === 0) {
      fieldErrors[0] = { client: true, service: true };
    }

    if (Object.keys(fieldErrors).length > 0 || done.length === 0) {
      setPassengersFieldErrors(fieldErrors);
      setPassengersDialogError(
        duplicate
          ? 'Each client can only appear once on a leg. Remove the duplicate row.'
          : 'Select a client and transportation service for each person (highlighted in red).'
      );
      return;
    }

    if (leg === 'first') {
      setFirstStopPassengers(done);
      setFormData(prev => ({ ...prev, client_id: done[0].client_id }));
    } else {
      setRouteLegs(prev => {
        const next = [...prev];
        if (next[leg]) next[leg] = { ...next[leg], passengers: done };
        return next;
      });
      if (leg === 0) {
        setFormData(prev => ({ ...prev, client_id: done[0].client_id }));
      }
    }
    setPassengersFieldErrors({});
    setPassengersDialogError(null);
    setPassengersDialog(null);
    setError(null);
  };

  const addDraftPassengerRow = () => {
    if (!passengersDialog) return;
    setPassengersDialog({
      ...passengersDialog,
      draft: [...passengersDialog.draft, { client_id: '', service_id: '', support_ids: [] }],
    });
  };

  const removeDraftPassengerRow = (index: number) => {
    if (!passengersDialog) return;
    const next = passengersDialog.draft.filter((_, j) => j !== index);
    setPassengersDialog({
      ...passengersDialog,
      draft: next.length > 0 ? next : [{ client_id: '', service_id: '' }],
    });
    setPassengersFieldErrors(prev => {
      const nextErr: Record<number, { client?: boolean; service?: boolean }> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k);
        if (i < index) nextErr[i] = v;
        else if (i > index) nextErr[i - 1] = v;
      });
      return nextErr;
    });
  };

  const updateDraftPassenger = (index: number, field: 'client_id' | 'service_id', value: number | '') => {
    if (!passengersDialog) return;
    const draft = passengersDialog.draft.map((row, j): DraftPassenger => {
      if (j !== index) return row;
      if (field === 'client_id') {
        const cid: number | '' = value === '' ? '' : Number(value);
        if (typeof cid === 'number' && !Number.isNaN(cid)) {
          const duplicate = passengersDialog.draft.some(
            (r, k) => k !== index && r.client_id !== '' && Number(r.client_id) === cid
          );
          if (duplicate) {
            setPassengersDialogError('Each client can only appear once on this leg.');
            setPassengersFieldErrors(prev => ({ ...prev, [index]: { ...(prev[index] || {}), client: true } }));
            return row;
          }
          void loadServicesForClient(cid);
          void loadSupportsForClient(cid);
          const sole = soleTransportationServiceId(serviceOptionsByClient[cid] || []);
          return { client_id: cid, service_id: sole, support_ids: [] };
        }
        return { client_id: cid, service_id: '', support_ids: [] };
      }
      const sid: number | '' = value === '' ? '' : Number(value);
      return { client_id: row.client_id, service_id: sid, support_ids: [] };
    });
    setPassengersDialog({ ...passengersDialog, draft });
    setPassengersFieldErrors(prev => {
      const rowErr = { ...(prev[index] || {}) };
      if (field === 'client_id') {
        delete rowErr.client;
        delete rowErr.service;
      } else {
        delete rowErr.service;
      }
      const next = { ...prev };
      if (rowErr.client || rowErr.service) next[index] = rowErr;
      else delete next[index];
      return next;
    });
    setPassengersDialogError(null);
  };

  const setDraftSupportIds = (index: number, supportIds: number[]) => {
    if (!passengersDialog) return;
    const draft = passengersDialog.draft.map((row, j) =>
      j === index ? { ...row, support_ids: supportIds } : row
    );
    setPassengersDialog({ ...passengersDialog, draft });
  };

  const dialogClients = useMemo(
    () =>
      clients.filter(c => {
        const id = Number(c.client_id);
        return transportationEligibleClientIds.has(id) || extraClientIdsInForm.has(id);
      }),
    [clients, transportationEligibleClientIds, extraClientIdsInForm]
  );

  const handleAddTransportation = () => {
    setEditingTransportation(null);
    setServiceOptionsByClient({});
    setSupportsByClient({});
    supportsLoadedRef.current = new Set();
    const link = visitLinkRef.current;
    const draftRows = initialDraftRowsRef.current;
    if (draftRows && draftRows.length > 0) {
      const asTypes = draftRows.map((r, i) => ({
        ...(r as object),
        transportation_id: i + 1,
      })) as TransportationType[];
      const parsed = parseRouteFromTransportationRows(asTypes);
      const first = asTypes[0];
      const draftStops =
        parsed.stops.length > 0
          ? parsed.stops.map((s, i) => ({
              location_id: s.location_id,
              label: (s as RouteStopState).label || defaultStopLocationName(i),
            }))
          : [
              { location_id: '' as const, label: defaultStopLocationName(0) },
              { location_id: '' as const, label: defaultStopLocationName(1) },
            ];
      const draftLegs =
        parsed.legs.length > 0
          ? parsed.legs
          : [
              {
                passengers: (link?.clientIds || [])
                  .filter(id => Number(id) > 0)
                  .map(client_id => ({ client_id: Number(client_id), service_id: 0 })),
                odometer_at_destination: '',
              },
            ];
      setRouteStops(draftStops);
      setRouteLegs(draftLegs);
      setFirstStopPassengers([]);
      const startOdo =
        first?.starting_miles != null && first.starting_miles !== undefined
          ? String(first.starting_miles)
          : '';
      const providerFromDraft =
        first?.provider_id != null && Number(first.provider_id) > 0
          ? Number(first.provider_id)
          : link?.providerId && link.providerId > 0
            ? link.providerId
            : selectedProvider || '';
      setFormData({
        provider_id: providerFromDraft,
        client_id: first?.client_id != null ? Number(first.client_id) : link?.clientIds?.[0] || '',
        vehicle_id: first?.vehicle_id != null && Number(first.vehicle_id) > 0 ? Number(first.vehicle_id) : '',
        transportation_date: first?.transportation_date
          ? String(first.transportation_date).split('T')[0]
          : link?.transportationDate
            ? String(link.transportationDate).split('T')[0]
            : easternTodayYmd(),
        scheduled_start: (first?.scheduled_start as string) || link?.scheduledStart || '',
        scheduled_end: (first?.scheduled_end as string) || link?.scheduledEnd || '',
        starting_miles: startOdo,
        notes: (first?.notes as string) || '',
      });
      const needLocIds = parsed.stops
        .map(s => s.location_id)
        .filter((id): id is number => id !== '' && id !== 0);
      if (needLocIds.length > 0) {
        Promise.all(needLocIds.map(id => api.locations.get(id)))
          .then(locs => {
            setLocationsMap(prev => {
              const next = { ...prev };
              locs.forEach((loc: Location) => {
                if (loc?.location_id) next[loc.location_id] = loc;
              });
              return next;
            });
          })
          .catch(() => {});
      }
      setOpenDialog(true);
      return;
    }

    const seedClients = (link?.clientIds || []).filter(id => Number(id) > 0);
    const seedPassengers = seedClients.map(client_id => ({
      client_id: Number(client_id),
      service_id: 0,
    }));
    // Open with a complete first segment (start + end) so locations and odometer are ready immediately.
    setRouteStops([
      { location_id: '', label: defaultStopLocationName(0) },
      { location_id: '', label: defaultStopLocationName(1) },
    ]);
    setRouteLegs([{ passengers: seedPassengers, odometer_at_destination: '' }]);
    setFirstStopPassengers([]);
    const dateFromLink = link?.transportationDate
      ? String(link.transportationDate).split('T')[0]
      : '';
    const providerFromLink = link?.providerId && link.providerId > 0 ? link.providerId : '';
    setFormData({
      provider_id: providerFromLink || selectedProvider || '',
      client_id: seedClients[0] || '',
      vehicle_id: '',
      transportation_date: dateFromLink || easternTodayYmd(),
      scheduled_start: link?.scheduledStart || '',
      scheduled_end: link?.scheduledEnd || '',
      starting_miles: '',
      notes: '',
    });
    setOpenDialog(true);
    if (seedClients.length > 0) {
      const pidForServices = Number(providerFromLink || selectedProvider || 0);
      setPassengersFieldErrors(
        Object.fromEntries(seedClients.map((_, i) => [i, { service: true as const }]))
      );
      setPassengersDialogError('Choose a transportation service for each client (highlighted in red).');
      setPassengersDialog({
        leg: 0,
        draft: seedClients.map(client_id => ({
          client_id: Number(client_id),
          service_id: '' as const,
          support_ids: [],
        })),
      });
      seedClients.forEach(id => {
        void loadServicesForClient(Number(id), pidForServices || undefined);
        void loadSupportsForClient(Number(id));
      });
    }
  };

  const performCloseMainDialog = useCallback(() => {
    setOpenDialog(false);
    setSubmitAttempted(false);
    if (isAddDialogOnly) onAddEntryClose?.();
  }, [isAddDialogOnly, onAddEntryClose]);

  const requestAbandonMainDialog = () => {
    setAbandonMainOpen(true);
  };

  const handleConfirmAbandonMain = () => {
    setAbandonMainOpen(false);
    performCloseMainDialog();
  };

  const handleCancelAbandonMain = () => {
    setAbandonMainOpen(false);
  };

  const handleMainDialogBackdrop = (_: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      requestAbandonMainDialog();
    }
  };

  const addEntryBootstrapped = useRef(false);
  useEffect(() => {
    if (!isAddDialogOnly || addEntryBootstrapped.current) return;
    addEntryBootstrapped.current = true;
    handleAddTransportation();
  }, [isAddDialogOnly]);

  const handleEditTransportation = async (t: TransportationType) => {
    setEditingTransportation(t);
    let parsed: ReturnType<typeof parseRouteFromTransportation>;
    let firstLegStartOdo = '';
    try {
      const parent = (await api.transportationParents.get(t.transportation_parent_id)) as TransportationParentWithTransportations;
      const rows = parent.transportations || [];
      const rowsSorted = [...rows].sort((a, b) => Number(a.transportation_id) - Number(b.transportation_id));
      if (rowsSorted[0]?.starting_miles != null && rowsSorted[0].starting_miles !== undefined) {
        firstLegStartOdo = String(rowsSorted[0].starting_miles);
      }
      if (rows.length === 1 && rows[0].details) {
        const fromJson = parseRouteFromTransportation(rows[0]);
        parsed = fromJson.stops.length >= 2 ? fromJson : parseRouteFromTransportationRows(rows);
      } else {
        parsed = parseRouteFromTransportationRows(rows);
      }
    } catch {
      parsed = parseRouteFromTransportation(t);
      if (t.starting_miles != null && t.starting_miles !== undefined) {
        firstLegStartOdo = String(t.starting_miles);
      }
    }
    setRouteStops(parsed.stops);
    setRouteLegs(parsed.legs);
    setFirstStopPassengers(parsed.firstStopPassengers);
    setServiceOptionsByClient({});
    setSupportsByClient({});
    supportsLoadedRef.current = new Set();
    setFormData({
      provider_id: t.provider_id,
      client_id: t.client_id,
      vehicle_id: t.vehicle_id != null && Number(t.vehicle_id) > 0 ? Number(t.vehicle_id) : '',
      transportation_date: t.transportation_date ? t.transportation_date.split('T')[0] : '',
      scheduled_start: t.scheduled_start || '',
      scheduled_end: t.scheduled_end || '',
      starting_miles: firstLegStartOdo,
      notes: t.notes || '',
    });
    const needLocIds = parsed.stops.map(s => s.location_id).filter((id): id is number => id !== '' && id !== 0);
    const missing = needLocIds.filter(id => !locationsMap[id]);
    if (missing.length > 0) {
      try {
        const locs = await Promise.all(missing.map(id => api.locations.get(id)));
        setLocationsMap(prev => {
          const n = { ...prev };
          locs.forEach((loc: Location) => {
            if (loc?.location_id) n[loc.location_id] = loc;
          });
          return n;
        });
      } catch {
        /* best-effort */
      }
    }
    if (t.provider_id) {
      try {
        const cl = await api.clients.listByProvider(t.provider_id, 500);
        setClients(Array.isArray(cl) ? cl : (cl as { items?: Client[] })?.items || []);
      } catch {
        /* ignore */
      }
    }
    parsed.firstStopPassengers.forEach(p => {
      if (p.client_id) {
        void loadServicesForClient(p.client_id);
        void loadSupportsForClient(p.client_id);
      }
    });
    parsed.legs.forEach(leg => {
      leg.passengers.forEach(p => {
        if (p.client_id) {
          void loadServicesForClient(p.client_id);
          void loadSupportsForClient(p.client_id);
        }
      });
    });
    setOpenDialog(true);
  };

  const handleFormChange = (field: keyof TransportationFormData) => (e: { target: { value: unknown } }) => {
    const value = e.target.value;
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'provider_id') {
        next.vehicle_id = '';
      }
      return next;
    });
  };

  const getStopOdometer = (stopIndex: number): string => {
    if (stopIndex === 0) return String(formData.starting_miles ?? '');
    return routeLegs[stopIndex - 1]?.odometer_at_destination ?? '';
  };

  const describeTransportIssues = () => {
    const locations: Record<number, boolean> = {};
    const odometers: Record<number, boolean> = {};
    const passengers: Record<number, boolean> = {};
    const providerMissing = !Number(formData.provider_id);
    const needSecondStop = routeStops.length < 2;
    const legsMismatch = routeStops.length >= 2 && routeLegs.length !== routeStops.length - 1;
    let firstPassengers = false;
    let duplicateMessage: string | null = null;

    routeStops.forEach((stop, i) => {
      if (routeStopLocationIdToNumber(stop.location_id) <= 0) locations[i] = true;
      if (parseOptionalOdometer(getStopOdometer(i)) === null) odometers[i] = true;
    });

    const passengersIncomplete = (list: { client_id?: number | string; service_id?: number | string }[]) =>
      list.length === 0 || list.some(p => !p.client_id || !p.service_id || Number(p.service_id) <= 0);

    if (needSecondStop) {
      firstPassengers = passengersIncomplete(firstStopPassengers);
    } else {
      routeLegs.forEach((leg, i) => {
        const list = leg.passengers || [];
        const seen = new Set<number>();
        let duplicate = false;
        for (const p of list) {
          const id = Number(p.client_id);
          if (!id) continue;
          if (seen.has(id)) duplicate = true;
          seen.add(id);
        }
        if (passengersIncomplete(list) || duplicate) passengers[i] = true;
        if (duplicate && !duplicateMessage) {
          duplicateMessage = `Leg ${i + 1}: each client can only appear once on a leg.`;
        }
      });
    }

    let message: string | null = null;
    if (providerMissing) message = 'Provider is required.';
    else if (needSecondStop) message = 'Add at least two stops to describe the route (start and destination).';
    else if (Object.values(locations).some(Boolean)) message = 'Set a location for every stop.';
    else if (legsMismatch) message = 'Route segments do not match stops. Try removing the last stop and adding it again.';
    else if (duplicateMessage) message = duplicateMessage;
    else if (Object.values(passengers).some(Boolean)) {
      message = 'Add at least one client (with a service) for every leg. Use “Add or edit clients” on each leg.';
    } else if (odometers[0]) message = 'Odometer reading is required at the first stop.';
    else {
      const later = Object.keys(odometers)
        .map(Number)
        .filter(i => i > 0 && odometers[i])
        .sort((a, b) => a - b);
      if (later.length) message = `Odometer reading is required at stop ${later[0] + 1}.`;
    }

    return {
      provider: providerMissing,
      needSecondStop,
      locations,
      odometers,
      passengers,
      firstPassengers,
      message,
    };
  };

  const emptyTransportIssues = {
    provider: false,
    needSecondStop: false,
    locations: {} as Record<number, boolean>,
    odometers: {} as Record<number, boolean>,
    passengers: {} as Record<number, boolean>,
    firstPassengers: false,
    message: null as string | null,
  };
  const transportIssues = submitAttempted ? describeTransportIssues() : emptyTransportIssues;

  const handleSubmit = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      setError(null);
      setSubmitAttempted(true);
      const issues = describeTransportIssues();
      if (issues.message) {
        setError(issues.message);
        return;
      }
      const providerId = Number(formData.provider_id);
      const primaryClientId = routeLegs[0].passengers[0]?.client_id;

      const linkVisitMap = visitLinkRef.current?.visitIdByClientId || null;
      const preservedVisitId =
        editingTransportation &&
        editingTransportation.visit_id != null &&
        String(editingTransportation.visit_id) !== ''
          ? Number(editingTransportation.visit_id)
          : null;
      const preservedTransportationDimId =
        editingTransportation &&
        editingTransportation.transportation_dim_id != null &&
        Number(editingTransportation.transportation_dim_id) > 0
          ? Number(editingTransportation.transportation_dim_id)
          : null;

      const desiredRows: Record<string, unknown>[] = [];
      for (let legIndex = 0; legIndex < routeLegs.length; legIndex++) {
        const leg = routeLegs[legIndex];
        const startId = Number(routeStops[legIndex].location_id);
        const endId = Number(routeStops[legIndex + 1].location_id);
        const startOdo =
          legIndex === 0
            ? parseOptionalOdometer(formData.starting_miles)
            : parseOptionalOdometer(routeLegs[legIndex - 1]?.odometer_at_destination);
        const endOdo = parseOptionalOdometer(leg.odometer_at_destination);
        const milesVal =
          startOdo != null && endOdo != null ? endOdo - startOdo : null;
        const clientsInVehicle = leg.passengers.length;
        const ratioForLeg = `1:${clientsInVehicle}`;
        for (const p of leg.passengers) {
          const clientId = p.client_id;
          const isPrimary = legIndex === 0 && clientId === leg.passengers[0].client_id;
          const linkedVisitId =
            linkVisitMap && linkVisitMap[clientId] != null ? Number(linkVisitMap[clientId]) : null;
          const row: Record<string, unknown> = {
            provider_id: providerId,
            client_id: clientId,
            transportation_dim_id: preservedTransportationDimId,
            vehicle_id: formData.vehicle_id ? Number(formData.vehicle_id) : null,
            service_id: p.service_id,
            start_location_id: startId,
            end_location_id: endId,
            transportation_date: formData.transportation_date || null,
            scheduled_start: formData.scheduled_start || null,
            scheduled_end: formData.scheduled_end || null,
            miles: milesVal,
            details: null,
            visit_id: linkedVisitId ?? preservedVisitId,
            ratio: ratioForLeg,
            support_ids: p.support_ids || [],
            starting_miles: startOdo,
            ending_miles: endOdo,
          };
          if (isPrimary) {
            row.notes = formData.notes ? formData.notes : null;
          } else {
            row.notes = null;
          }
          desiredRows.push(row);
        }
      }

      let getExcludeTid: (i: number) => number | undefined = () => undefined;
      if (editingTransportation) {
        const parent0 = (await api.transportationParents.get(
          editingTransportation.transportation_parent_id
        )) as TransportationParentWithTransportations;
        const forExclude = [...(parent0.transportations || [])].sort(
          (a, b) => Number(a.transportation_id) - Number(b.transportation_id)
        );
        getExcludeTid = (i) => (forExclude[i] ? Number(forExclude[i].transportation_id) : undefined);
      }
      const tcheck = desiredRows.map(row => ({
        client_id: Number(row.client_id),
        scheduled_start: (row.scheduled_start as string | null) || null,
        scheduled_end: (row.scheduled_end as string | null) || null,
      }));
      const tw = await runTransportationRowsOverlapCheck(tcheck, {
        getExcludeTransportationId: i => {
          const fromEdit = getExcludeTid(i);
          if (fromEdit != null) return fromEdit;
          const tid = desiredRows[i]?.transportation_id;
          return tid != null && Number(tid) > 0 ? Number(tid) : undefined;
        },
        getExcludeVisitId: i => {
          const linkVisitMap = visitLinkRef.current?.visitIdByClientId || null;
          const cid = Number(desiredRows[i]?.client_id);
          if (linkVisitMap && cid && linkVisitMap[cid] != null) return Number(linkVisitMap[cid]);
          const rowVid = desiredRows[i]?.visit_id;
          return rowVid != null && Number(rowVid) > 0 ? Number(rowVid) : undefined;
        },
      });
      if (tw && !window.confirm(`${tw}\n\nSave anyway?`)) {
        return;
      }

      if (onDraftSaveRef.current) {
        onDraftSaveRef.current(desiredRows);
        performCloseMainDialog();
        return;
      }

      let savedParentId: number;
      if (editingTransportation) {
        const parentId = editingTransportation.transportation_parent_id;
        savedParentId = parentId;
        const parent = (await api.transportationParents.get(parentId)) as TransportationParentWithTransportations;
        const existing = [...(parent.transportations || [])].sort(
          (a, b) => Number(a.transportation_id) - Number(b.transportation_id)
        );
        const n = desiredRows.length;
        const m = existing.length;
        for (let i = 0; i < Math.min(m, n); i++) {
          await api.transportation.update(existing[i].transportation_id, {
            ...desiredRows[i],
            transportation_parent_id: parentId,
          });
        }
        for (let i = m - 1; i >= n; i--) {
          await api.transportation.delete(existing[i].transportation_id);
        }
        for (let i = m; i < n; i++) {
          await api.transportation.create({
            ...desiredRows[i],
            transportation_parent_id: parentId,
          });
        }
      } else {
        const parent = (await api.transportationParents.create({ provider_id: providerId })) as {
          transportation_parent_id: number;
        };
        savedParentId = parent.transportation_parent_id;
        for (const row of desiredRows) {
          await api.transportation.create({
            ...row,
            transportation_parent_id: savedParentId,
          });
        }
      }
      setSuccess(editingTransportation ? 'Transportation updated successfully.' : 'Transportation created successfully.');
      performCloseMainDialog();
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to save transportation.'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDeleteOccurrence = async (transportationParentId: number) => {
    if (!window.confirm('Delete this transportation occurrence and all line items for it?')) return;
    try {
      await api.transportationParents.delete(transportationParentId);
      setSuccess('Transportation occurrence deleted.');
      fetchData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to delete.'));
    }
  };

  const openLocationDialog = (stopIndex: number) => {
    setLocationStopIndex(stopIndex);
    const locIdNum = routeStopLocationIdToNumber(routeStops[stopIndex]?.location_id);
    const loc = locIdNum ? locationsMap[locIdNum] : undefined;
    if (loc) {
      setLocationFormData({
        location_address_line_1: (loc.location_address_line_1 || '').toUpperCase(),
        location_address_line_2: (loc.location_address_line_2 || '').toUpperCase(),
        location_city: (loc.location_city || '').toUpperCase(),
        location_state: (loc.location_state || '').toUpperCase(),
        location_zip: (loc.location_zip || '').toUpperCase(),
        location_country: (loc.location_country || 'US').toUpperCase(),
        location_latitude: loc.location_latitude as number | undefined,
        location_longitude: loc.location_longitude as number | undefined,
      });
    } else {
      setLocationFormData({
        location_address_line_1: '',
        location_address_line_2: '',
        location_city: '',
        location_state: '',
        location_zip: '',
        location_country: 'US',
        location_latitude: undefined,
        location_longitude: undefined,
      });
    }
    setLocationDialogOpen(true);
  };

  const locationPickerProviderId = useMemo(() => {
    if (formData.provider_id === '' || formData.provider_id === undefined) return null;
    const n = Number(formData.provider_id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [formData.provider_id]);

  const fetchProviderHistoryStable = useCallback(async (providerId: number) => {
    const r = await api.locations.listByProvider(providerId);
    return Array.isArray(r) ? (r as Location[]) : [];
  }, []);

  const siblingStopsForPicker = useMemo(() => {
    return routeStops
      .map((s, idx) => {
        const locId = routeStopLocationIdToNumber(s.location_id);
        const loc = locId ? locationsMap[locId] : undefined;
        return {
          idx,
          label: s.label || formatLocation(loc) || defaultStopLocationName(idx),
          location: loc as Location | undefined,
        };
      })
      .filter(x => x.idx !== locationStopIndex && x.location)
      .map(x => ({ label: x.label, location: x.location as Location }));
  }, [routeStops, locationStopIndex, locationsMap]);

  const resolvePickedLocationForStop = useCallback(
    (loc: Location) => {
      if (!loc?.location_id) return;
      setLocationsMap(prev => ({ ...prev, [loc.location_id]: loc }));
      setRouteStops(prev => {
        const next = [...prev];
        if (next[locationStopIndex]) {
          next[locationStopIndex] = { ...next[locationStopIndex], location_id: loc.location_id };
        }
        return next;
      });
      setLocationDialogOpen(false);
      setError(null);
    },
    [locationStopIndex]
  );

  const setLegDestinationOdometer = (legIndex: number, odometer_at_destination: string) => {
    setRouteLegs(prev => {
      const next = [...prev];
      if (next[legIndex]) next[legIndex] = { ...next[legIndex], odometer_at_destination };
      return next;
    });
  };

  const setStopOdometer = (stopIndex: number, value: string) => {
    if (stopIndex === 0) {
      setFormData(prev => ({ ...prev, starting_miles: value }));
      return;
    }
    setLegDestinationOdometer(stopIndex - 1, value);
  };

  const addStop = () => {
    setRouteStops(prev => [
      ...prev,
      { location_id: '', label: defaultStopLocationName(prev.length) },
    ]);
    setRouteLegs(prev => {
      const nextLeg: RouteLegState =
        prev.length === 0
          ? { passengers: firstStopPassengers.map(x => ({ ...x })), odometer_at_destination: '' }
          : {
              passengers: (prev[prev.length - 1]?.passengers || []).map(x => ({ ...x })),
              odometer_at_destination: '',
            };
      return [...prev, nextLeg];
    });
  };

  const removeLastStop = () => {
    if (routeStops.length <= 1) return;
    if (routeStops.length === 2) {
      setFirstStopPassengers(
        routeLegs[0]?.passengers?.length ? routeLegs[0].passengers.map(x => ({ ...x })) : []
      );
    }
    setRouteStops(prev => prev.slice(0, -1));
    setRouteLegs(prev => prev.slice(0, -1));
  };

  const canAddReturnTrip = routeStops.length === 2 && Boolean(routeStops[0]?.location_id);
  const returnTripDisabledReason = !routeStops[0]?.location_id
    ? 'Set a location on the first stop before adding a return trip.'
    : undefined;

  const addReturnTrip = () => {
    if (routeStops.length !== 2) return;
    const startLocationId = routeStops[0]?.location_id;
    if (!startLocationId) {
      setError('Set a location on the first stop before adding a return trip.');
      return;
    }
    const startOdo = parseOptionalOdometer(formData.starting_miles);
    const destOdo = parseOptionalOdometer(routeLegs[0]?.odometer_at_destination);
    let returnOdo = '';
    if (startOdo != null && destOdo != null) {
      const miles = destOdo - startOdo;
      if (miles >= 0) {
        returnOdo = String(Number((destOdo + miles).toFixed(1)));
      }
    }
    setRouteStops(prev => [
      ...prev,
      { location_id: startLocationId, label: 'Return to start' },
    ]);
    setRouteLegs(prev => [
      ...prev,
      {
        passengers: (prev[0]?.passengers || []).map(x => ({ ...x })),
        odometer_at_destination: returnOdo,
      },
    ]);
  };

  const filtered = getFilteredTransportationOccurrences();
  const dialogProviderSelected = Number(formData.provider_id) > 0;

  if (isLoading && transportationOccurrences.length === 0 && !isAddDialogOnly) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: isAddDialogOnly ? 0 : 2 }}>
      {!isAddDialogOnly && (
        <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#2D4B73', minWidth: 0 }}>
          Transportation
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTransportation}
          sx={{ backgroundColor: '#2D4B73', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add Transportation
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Add
          </Box>
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Card sx={{ mb: 3, backgroundColor: '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filter Transportation
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Provider</InputLabel>
                <Select
                  value={filters.providerId}
                  label="Filter by Provider"
                  onChange={(e: SelectChangeEvent<string>) => setFilters(prev => ({ ...prev, providerId: e.target.value }))}
                  sx={fieldOnPageBlueSx}
                  MenuProps={selectMenuWhitePaperProps}
                >
                  <MenuItem value="">All Providers</MenuItem>
                  {providers.map(p => (
                    <MenuItem key={String(p.provider_id)} value={p.provider_id as number}>
                      {p.first_name} {p.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Client</InputLabel>
                <Select
                  value={filters.clientId}
                  label="Filter by Client"
                  onChange={(e: SelectChangeEvent<string>) => setFilters(prev => ({ ...prev, clientId: e.target.value }))}
                  sx={fieldOnPageBlueSx}
                  MenuProps={selectMenuWhitePaperProps}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {clients.map(c => (
                    <MenuItem key={String(c.client_id)} value={c.client_id as number}>
                      {c.first_name} {c.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="Start Date"
                value={filters.startDate || null}
                onChange={v => setFilters(prev => ({ ...prev, startDate: v || '' }))}
                usePageBlueStyle
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AppDateField
                label="End Date"
                value={filters.endDate || null}
                onChange={v => setFilters(prev => ({ ...prev, endDate: v || '' }))}
                usePageBlueStyle
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={() => setFilters({ providerId: '', clientId: '', startDate: '', endDate: '' })}
                sx={{ mt: 1, backgroundColor: '#2D4B73', '&:hover': { backgroundColor: '#1E3452' } }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#2D4B73' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Service</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Ratio</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Miles</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(occ => {
              const t = [...(occ.transportations || [])].sort((a, b) => a.transportation_id - b.transportation_id)[0];
              if (!t) return null;
              const sortedTs = [...(occ.transportations || [])].sort(
                (a, b) => a.transportation_id - b.transportation_id
              );
              const ratioSeen = new Set<string>();
              const ratioLabels: string[] = [];
              for (const x of sortedTs) {
                const r = x.ratio;
                if (r && !ratioSeen.has(r)) {
                  ratioSeen.add(r);
                  ratioLabels.push(r);
                }
              }
              const ratioCellText = ratioLabels.length ? ratioLabels.join(', ') : '—';
              const clientIdsSeen = new Set<number>();
              const clientNames: string[] = [];
              for (const x of occ.transportations || []) {
                const cid = Number(x.client_id);
                if (!cid || clientIdsSeen.has(cid)) continue;
                clientIdsSeen.add(cid);
                clientNames.push(getClientName(cid));
              }
              const clientLabel = clientNames.join(', ');
              const serviceIdsSeen = new Set<number>();
              (occ.transportations || []).forEach(x => {
                if (x.service_id != null && !Number.isNaN(Number(x.service_id))) {
                  serviceIdsSeen.add(Number(x.service_id));
                }
              });
              const serviceLabel =
                serviceIdsSeen.size > 0
                  ? Array.from(serviceIdsSeen, id => getServiceCode(id)).join(', ')
                  : '—';
              const tripMiles = totalParentTripMiles(sortedTs);
              return (
                <TableRow key={occ.transportation_parent_id} hover>
                  <TableCell>{formatDate(t.transportation_date || t.created_at)}</TableCell>
                  <TableCell>{getProviderName(t.provider_id)}</TableCell>
                  <TableCell>{clientLabel || getClientName(Number(t.client_id))}</TableCell>
                  <TableCell>{serviceLabel}</TableCell>
                  <TableCell>{ratioCellText}</TableCell>
                  <TableCell>{tripMiles != null ? tripMiles : '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEditTransportation(t)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteOccurrence(occ.transportation_parent_id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filtered.length === 0 && (
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>No transportation records found.</Typography>
      )}
        </>
      )}

      {isAddDialogOnly && error && (
        <Alert severity="error" sx={{ mb: 2, mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {isAddDialogOnly && success && (
        <Alert severity="success" sx={{ mb: 2, mx: 2, mt: 1 }}>{success}</Alert>
      )}

      <Dialog open={openDialog} onClose={handleMainDialogBackdrop} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingTransportation ? 'Edit Transportation' : 'Add Transportation'}
          <IconButton onClick={requestAbandonMainDialog} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required error={transportIssues.provider}>
                <InputLabel>Provider *</InputLabel>
                <Select value={formData.provider_id} label="Provider *" onChange={handleFormChange('provider_id')}>
                  <MenuItem value="">
                    <em>Select provider</em>
                  </MenuItem>
                  {providers.map(p => (
                    <MenuItem key={String(p.provider_id)} value={p.provider_id as number}>
                      {p.first_name} {p.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {transportIssues.provider && <FormHelperText>Provider is required</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <AppDateField
                label="Date"
                value={formData.transportation_date || null}
                onChange={v =>
                  setFormData(prev => ({ ...prev, transportation_date: v || '' }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Vehicle</InputLabel>
                <Select
                  value={formData.vehicle_id}
                  label="Vehicle"
                  onChange={handleFormChange('vehicle_id')}
                  disabled={!formData.provider_id}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {vehicles.map(v => (
                    <MenuItem key={v.vehicle_id} value={v.vehicle_id}>
                      {v.license_plate} — {v.make} {v.model}
                      {v.year ? ` (${v.year})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mt: 2,
              mb: 0.5,
              bgcolor: 'action.hover',
            }}
          >
            <Stack spacing={0}>
              {/* Narrow: stacked segment flow with timeline on the right */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {routeStops.length < 2 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0,1fr) 40px',
                      columnGap: 1,
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ minWidth: 0, pr: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NARROW_SEGMENT_HEADER, mb: 1 }}>
                        First Segment
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<GroupAddIcon />}
                        onClick={() => openPassengersDialog('first')}
                        disabled={!dialogProviderSelected}
                        {...routeActionButtonProps(
                          firstStopPassengers.length > 0,
                          transportIssues.firstPassengers,
                          { display: 'block', width: '100%' },
                        )}
                      >
                        Add or Edit Clients
                      </Button>
                      {!dialogProviderSelected && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Select a provider first to add clients.
                        </Typography>
                      )}
                      <NarrowClientList passengers={firstStopPassengers} getClientName={getClientName} />
                      <Button
                        size="small"
                        startIcon={<LocationIcon />}
                        onClick={() => openLocationDialog(0)}
                        {...routeActionButtonProps(Boolean(routeStops[0]?.location_id), !!transportIssues.locations[0], {
                          mb: 0.5,
                          display: 'block',
                          width: '100%',
                        })}
                      >
                        {routeStops[0]?.location_id
                          ? 'Tap to Change Start Location'
                          : 'Tap to Add Start Location'}
                      </Button>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Start Location:{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {routeStops[0]?.location_id
                            ? formatLocation(locationsMap[Number(routeStops[0].location_id)]) || 'Set'
                            : 'Not set'}
                        </Box>
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        required
                        label="Starting Odometer"
                        type="number"
                        value={getStopOdometer(0)}
                        onChange={e => setStopOdometer(0, e.target.value)}
                        inputProps={{ min: 0, step: 0.1 }}
                        error={!!transportIssues.odometers[0]}
                        helperText={transportIssues.odometers[0] ? 'Required' : undefined}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <RouteNodeMarker />
                      <Box sx={{ width: 3, flex: 1, minHeight: 24, bgcolor: LINE_BG, borderRadius: '1px' }} />
                    </Box>
                  </Box>
                ) : (
                  routeLegs.map((leg, i) => {
                    const destIndex = i + 1;
                    const destStop = routeStops[destIndex];
                    const isFirst = i === 0;
                    const startStop = routeStops[0];
                    return (
                      <Box
                        key={`narrow-seg-${i}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0,1fr) 40px',
                          columnGap: 1,
                          alignItems: 'stretch',
                          mb: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0, pr: 1.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NARROW_SEGMENT_HEADER, mb: 1 }}>
                            {isFirst ? 'First Segment' : `Segment ${i + 1}`}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<GroupAddIcon />}
                            onClick={() => openPassengersDialog(i)}
                            disabled={!dialogProviderSelected}
                            {...routeActionButtonProps((leg.passengers || []).length > 0, !!transportIssues.passengers[i], {
                              display: 'block',
                              width: '100%',
                            })}
                          >
                            Add or Edit Clients
                          </Button>
                          {!dialogProviderSelected && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Select a provider first to add clients.
                            </Typography>
                          )}
                          <NarrowClientList passengers={leg.passengers || []} getClientName={getClientName} />
                          {isFirst && (
                            <>
                              <Button
                                size="small"
                                startIcon={<LocationIcon />}
                                onClick={() => openLocationDialog(0)}
                                {...routeActionButtonProps(Boolean(startStop?.location_id), !!transportIssues.locations[0], {
                                  mb: 0.5,
                                  display: 'block',
                                  width: '100%',
                                })}
                              >
                                {startStop?.location_id
                                  ? 'Tap to Change Start Location'
                                  : 'Tap to Add Start Location'}
                              </Button>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                Start Location:{' '}
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                  {startStop?.location_id
                                    ? formatLocation(locationsMap[Number(startStop.location_id)]) || 'Set'
                                    : 'Not set'}
                                </Box>
                              </Typography>
                              <TextField
                                size="small"
                                fullWidth
                                required
                                label="Starting Odometer"
                                type="number"
                                value={getStopOdometer(0)}
                                onChange={e => setStopOdometer(0, e.target.value)}
                                inputProps={{ min: 0, step: 0.1 }}
                                error={!!transportIssues.odometers[0]}
                                helperText={transportIssues.odometers[0] ? 'Required' : undefined}
                                sx={{ mb: 2 }}
                              />
                            </>
                          )}
                          <Button
                            size="small"
                            startIcon={<LocationIcon />}
                            onClick={() => openLocationDialog(destIndex)}
                            {...routeActionButtonProps(Boolean(destStop?.location_id), !!transportIssues.locations[destIndex], {
                              mb: 0.5,
                              display: 'block',
                              width: '100%',
                            })}
                          >
                            {destStop?.location_id
                              ? `Tap to Change Stop ${destIndex} Location`
                              : `Tap to Add Stop ${destIndex} Location`}
                          </Button>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {`Stop ${destIndex} Location: `}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {destStop?.location_id
                                ? formatLocation(locationsMap[Number(destStop.location_id)]) || 'Set'
                                : 'Not set'}
                            </Box>
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            required
                            label={`Stop ${destIndex} Odometer`}
                            type="number"
                            value={getStopOdometer(destIndex)}
                            onChange={e => setStopOdometer(destIndex, e.target.value)}
                            inputProps={{ min: 0, step: 0.1 }}
                            error={!!transportIssues.odometers[destIndex]}
                            helperText={transportIssues.odometers[destIndex] ? 'Required' : undefined}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          {isFirst ? (
                            <RouteNodeMarker />
                          ) : (
                            <Box sx={{ width: 3, height: 16, bgcolor: LINE_BG, borderRadius: '1px' }} />
                          )}
                          <Box
                            sx={{
                              width: 3,
                              flex: 1,
                              minHeight: 24,
                              bgcolor: LINE_BG,
                              borderRadius: '1px',
                              my: '-2px',
                            }}
                          />
                          <RouteNodeMarker />
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              {/* Wide: original transit map — location | node/line | clients */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {routeStops.map((stop, i) => (
                  <React.Fragment key={`wide-stop-${i}`}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0,1fr) 44px minmax(0,1fr)',
                        columnGap: 1,
                        rowGap: 0,
                        alignItems: 'start',
                        py: 0,
                      }}
                    >
                      <Box sx={{ textAlign: 'right', pr: 1, py: 0.5 }}>
                        <Button
                          size="small"
                          startIcon={<LocationIcon />}
                          onClick={() => openLocationDialog(i)}
                          {...routeActionButtonProps(Boolean(stop.location_id), !!transportIssues.locations[i], { mb: 0.5 })}
                        >
                          {stop.location_id
                            ? i === 0
                              ? 'Tap to Change Start Location'
                              : `Tap to Change Stop ${i} Location`
                            : i === 0
                              ? 'Tap to Add Start Location'
                              : `Tap to Add Stop ${i} Location`}
                        </Button>
                        {stop.location_id ? (
                          <Typography variant="body2" color="text.primary" sx={{ display: 'block', mb: 1 }}>
                            {formatLocation(locationsMap[Number(stop.location_id)])}
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            color={transportIssues.locations[i] ? 'error' : 'text.secondary'}
                            sx={{ display: 'block', mb: 1 }}
                          >
                            Location required before saving
                          </Typography>
                        )}
                        <TextField
                          size="small"
                          fullWidth
                          required
                          label="Odometer reading"
                          type="number"
                          value={getStopOdometer(i)}
                          onChange={e => setStopOdometer(i, e.target.value)}
                          inputProps={{ min: 0, step: 0.1 }}
                          error={!!transportIssues.odometers[i]}
                          helperText={transportIssues.odometers[i] ? 'Required' : undefined}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          alignSelf: 'stretch',
                          minHeight: 44,
                        }}
                      >
                        <RouteNodeMarker />
                      </Box>
                      <Box sx={{ pl: 1 }}>
                        {i === 0 && routeStops.length === 1 && (
                          <Box>
                            <SegmentPassengerSummary
                              passengers={firstStopPassengers}
                              getClientName={getClientName}
                              getServiceCode={getServiceCode}
                            />
                            <Button
                              size="small"
                              startIcon={<GroupAddIcon />}
                              onClick={() => openPassengersDialog('first')}
                              disabled={!dialogProviderSelected}
                              {...routeActionButtonProps(firstStopPassengers.length > 0, transportIssues.firstPassengers)}
                            >
                              Add or edit clients (first leg)
                            </Button>
                            {!dialogProviderSelected && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Select a provider first to add clients.
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Open the pop-up to add one row per person: choose the client, then their transportation service.
                              Remove a row to take someone off this leg. Only one entry per client per leg.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {i === 0 && routeStops.length === 1 && (
                      <Stack spacing={1} sx={{ mt: 1.5, mb: 0.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={addStop}
                          sx={{ backgroundColor: LINE_BG, alignSelf: 'flex-start' }}
                        >
                          Add stop
                        </Button>
                        <Typography variant="caption" color={transportIssues.needSecondStop ? 'error' : 'text.secondary'}>
                          Add another stop to complete a leg, then set clients and services for that segment.
                        </Typography>
                      </Stack>
                    )}

                    {i < routeStops.length - 1 && (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0,1fr) 44px minmax(0,1fr)',
                          columnGap: 1,
                          rowGap: 0,
                          minHeight: 100,
                          alignItems: 'stretch',
                          mt: '-1px',
                          mb: '-1px',
                        }}
                      >
                        <Box />
                        <RouteConnectorLine minHeight={72} />
                        <Box sx={{ pl: 1, pb: 1 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {i === 0 ? 'First Segment' : `Segment ${i + 1}`}
                          </Typography>
                          <SegmentPassengerSummary
                            passengers={routeLegs[i]?.passengers || []}
                            getClientName={getClientName}
                            getServiceCode={getServiceCode}
                          />
                          <Button
                            size="small"
                            startIcon={<GroupAddIcon />}
                            onClick={() => openPassengersDialog(i)}
                            disabled={!dialogProviderSelected}
                            {...routeActionButtonProps(
                              (routeLegs[i]?.passengers || []).length > 0,
                              !!transportIssues.passengers[i],
                              { display: 'block' },
                            )}
                          >
                            Add or edit clients
                          </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            Add/Edit one or more clients.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Stack>

            {routeStops.length >= 2 && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ display: { xs: 'none', sm: 'flex' }, mt: 2 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button variant="contained" size="small" onClick={addStop} sx={{ backgroundColor: LINE_BG }}>
                  Add stop
                </Button>
                {routeStops.length === 2 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReplayIcon />}
                    onClick={addReturnTrip}
                    disabled={!canAddReturnTrip}
                    title={
                      returnTripDisabledReason ||
                      'Adds a second segment that returns to the first stop with the same mileage'
                    }
                  >
                    Add return trip
                  </Button>
                )}
                {routeStops.length > 2 && (
                  <Button variant="text" size="small" color="error" onClick={removeLastStop}>
                    Remove last stop
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={2}
            value={formData.notes || ''}
            onChange={handleFormChange('notes')}
            inputProps={{ maxLength: 500 }}
            sx={{ mt: 2 }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="stretch"
            sx={{ display: { xs: 'flex', sm: 'none' }, mt: 2 }}
          >
            <Button
              variant="contained"
              onClick={addStop}
              sx={{
                flex: 3,
                backgroundColor: NARROW_ACTION_PURPLE,
                '&:hover': { backgroundColor: NARROW_ACTION_PURPLE_HOVER },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {routeStops.length <= 1 ? 'Add stop' : `Add ${segmentOrdinalWord(routeStops.length)} Segment`}
            </Button>
            {routeStops.length === 2 && (
              <Button
                variant="contained"
                onClick={addReturnTrip}
                disabled={!canAddReturnTrip}
                sx={{
                  flex: 2,
                  backgroundColor: NARROW_ACTION_PURPLE,
                  '&:hover': { backgroundColor: NARROW_ACTION_PURPLE_HOVER },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  lineHeight: 1.2,
                  px: 1,
                }}
              >
                Add Return Trip
              </Button>
            )}
          </Stack>
          {routeStops.length > 2 && (
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={removeLastStop}
              sx={{ display: { xs: 'inline-flex', sm: 'none' }, mt: 0.5 }}
            >
              Remove last stop
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={requestAbandonMainDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ backgroundColor: '#2D4B73' }}>
            {saving ? 'Saving…' : 'Done'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={abandonMainOpen} onClose={handleCancelAbandonMain}>
        <DialogTitle>Abandon transportation?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to abandon this transportation? All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAbandonMain}>Cancel</Button>
          <Button onClick={handleConfirmAbandonMain} variant="contained" color="error">
            Abandon transportation
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!passengersDialog} onClose={() => setPassengersDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {passengersDialog?.leg === 'first' || passengersDialog?.leg === 0
            ? 'Clients on the first segment'
            : `Clients on segment ${passengersDialog ? (passengersDialog.leg as number) + 1 : ''}`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a row for each person on this segment. Choose the client, then the transportation service for that person. Use
            the remove button to take someone off this leg. To change a client or service, pick a different value in the row.
          </Typography>
          {passengersDialog && (
            <Stack spacing={2}>
              {passengersDialogError && (
                <Alert severity="error" onClose={() => setPassengersDialogError(null)}>
                  {passengersDialogError}
                </Alert>
              )}
              {passengersDialog.draft.map((row, idx) => {
                const cid = row.client_id === '' ? 0 : Number(row.client_id);
                const servicesForRow = cid ? serviceOptionsByClient[cid] || [] : [];
                const rowErr = passengersFieldErrors[idx] || {};
                return (
                  <Box
                    key={`draft-${idx}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
                      gap: 1,
                      alignItems: 'start',
                    }}
                  >
                    <FormControl fullWidth size="small" error={!!rowErr.client} required>
                      <InputLabel>Client</InputLabel>
                      <Select
                        label="Client"
                        value={row.client_id === '' ? '' : row.client_id}
                        onChange={e => {
                          const v = e.target.value;
                          updateDraftPassenger(
                            idx,
                            'client_id',
                            v === '' ? '' : typeof v === 'string' ? Number(v) : (v as number)
                          );
                        }}
                      >
                        <MenuItem value="">
                          <em>Select client</em>
                        </MenuItem>
                        {dialogClients
                          .filter(c => {
                            const id = Number(c.client_id);
                            const usedElsewhere = passengersDialog.draft.some(
                              (r, j) => j !== idx && r.client_id !== '' && Number(r.client_id) === id
                            );
                            return !usedElsewhere || (row.client_id !== '' && Number(row.client_id) === id);
                          })
                          .map(c => (
                            <MenuItem key={String(c.client_id)} value={c.client_id as number}>
                              {c.first_name} {c.last_name}
                            </MenuItem>
                          ))}
                      </Select>
                      {rowErr.client && <FormHelperText>Client is required</FormHelperText>}
                    </FormControl>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={row.client_id === ''}
                      error={!!rowErr.service}
                      required
                    >
                      <InputLabel>Transportation service</InputLabel>
                      <Select
                        label="Transportation service"
                        value={row.service_id === '' ? '' : row.service_id}
                        onChange={e => {
                          const v = e.target.value;
                          updateDraftPassenger(
                            idx,
                            'service_id',
                            v === '' ? '' : typeof v === 'string' ? Number(v) : (v as number)
                          );
                        }}
                      >
                        <MenuItem value="">
                          <em>Select service</em>
                        </MenuItem>
                        {servicesForRow.map(s => (
                          <MenuItem key={String(s.service_id)} value={s.service_id as number}>
                            {[s.code, s.service_unit].filter(Boolean).join(' | ')}
                          </MenuItem>
                        ))}
                      </Select>
                      {rowErr.service && (
                        <FormHelperText>
                          {row.client_id === ''
                            ? 'Select a client first'
                            : servicesForRow.length === 0
                              ? 'No transportation service available for this client'
                              : 'Transportation service is required'}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', sm: 'center' } }}>
                      <IconButton
                        size="small"
                        aria-label="Remove client from leg"
                        onClick={() => removeDraftPassengerRow(idx)}
                        color="error"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <FormControl fullWidth size="small" disabled={row.client_id === ''} sx={{ gridColumn: '1 / -1' }}>
                      <InputLabel>Supports</InputLabel>
                      <Select
                        multiple
                        label="Supports"
                        value={row.support_ids || []}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as number[]).map(id => {
                              const support = (supportsByClient[cid] || []).find(s => s.support_id === id);
                              return <Chip key={id} size="small" label={support?.support_title || `Support ${id}`} />;
                            })}
                          </Box>
                        )}
                        onChange={e => {
                          const raw = e.target.value;
                          const ids = (typeof raw === 'string' ? raw.split(',') : raw)
                            .map(value => Number(value))
                            .filter(id => id > 0);
                          setDraftSupportIds(idx, ids);
                        }}
                      >
                        {supportsForSelection(supportsByClient[cid], row.service_id, row.support_ids || []).map(support => (
                          <MenuItem key={support.support_id} value={support.support_id}>
                            {support.support_title || `Support ${support.support_id}`}
                            {support.active_status === false ? ' (inactive)' : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                );
              })}
              <Button size="small" startIcon={<AddIcon />} onClick={addDraftPassengerRow} variant="outlined">
                Add another client
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPassengersDialog(null);
              setPassengersFieldErrors({});
              setPassengersDialogError(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={applyPassengersDialog} sx={{ backgroundColor: '#2D4B73' }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <TransportationLocationDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        title="Location"
        draft={locationFormData}
        onDraftChange={setLocationFormData}
        providerId={locationPickerProviderId}
        clients={dialogClients}
        siblingStops={siblingStopsForPicker}
        onLocationResolved={resolvePickedLocationForStop}
        createLocation={(body: Record<string, unknown>) =>
          api.locations.create(body) as unknown as Promise<Location>
        }
        fetchProviderHistory={fetchProviderHistoryStable}
        reportError={setError}
      />
    </Box>
  );
};

export default TransportationPage;
